/**
 * Decision Summary Module
 * 
 * Generates human-readable decision summaries based on pricing,
 * performance, and space considerations.
 */

import type { SystemRecOutput, RecommendationOption } from './types';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Build a decision summary paragraph
 * 
 * Analyzes all options and generates:
 * - Best value (based on total cost)
 * - Best performance (based on score)
 * - Best space (based on system type analysis)
 * - A combined narrative paragraph
 */
export function buildDecisionSummary(output: SystemRecOutput): SystemRecOutput['decisionSummary'] {
  const allOptions = [output.primary, ...output.alternatives];
  
  // Best value analysis (requires pricing data)
  let valueLine: string | null = null;
  const optionsWithCost = allOptions.filter(opt => 
    opt.pricing?.totalCost !== undefined && 
    typeof opt.pricing.totalCost === 'number'
  );
  
  if (optionsWithCost.length >= 2) {
    // Sort by total cost (ascending)
    const sorted = [...optionsWithCost].sort((a, b) => 
      (a.pricing!.totalCost!) - (b.pricing!.totalCost!)
    );
    
    const bestValue = sorted[0];
    const runnerUp = sorted[1];
    const delta = runnerUp.pricing!.totalCost! - bestValue.pricing!.totalCost!;
    
    const years = bestValue.pricing!.years;
    const currency = bestValue.pricing!.currency;
    const capex = bestValue.pricing!.capex ?? 0;
    const totalCost = bestValue.pricing!.totalCost!;
    
    valueLine = `Best value over ${years} years is ${bestValue.title}: ${currency} ${Math.round(totalCost)} total (${currency} ${Math.round(capex)} installed + running costs).`;
    
    if (delta > 0) {
      valueLine += ` That's ${currency} ${Math.round(delta)} cheaper than ${runnerUp.title}.`;
    }
  } else if (output.pricingSummary?.overridesProvided && optionsWithCost.length < allOptions.length) {
    valueLine = "Prices were provided for some options, but totals couldn't be calculated for all options.";
  }
  
  // Best performance analysis - metric-based instead of just score
  // Determine what "best" means based on available data
  let perfLine: string | null = null;
  
  // Extract user preferences from input if available
  const inputEcho = output.inputEcho;
  const costPriority = inputEcho?.costPriority || 'balanced';
  const budgetVsGreen = inputEcho?.budgetVsGreen || 'balanced';
  
  // Determine performance metric based on user priorities
  let performanceMetric: 'cost' | 'carbon' | 'score' = 'score';
  
  // Check if year1 data is available (for running cost/carbon metrics)
  const hasYear1Data = output.year1?.options && output.year1.options.length > 0;
  
  if (costPriority === 'low_cost' || costPriority === 'budget_conscious') {
    performanceMetric = 'cost';
  } else if (budgetVsGreen === 'decarbonise_now' || budgetVsGreen === 'green_priority') {
    performanceMetric = 'carbon';
  } else if (hasYear1Data && costPriority === 'balanced') {
    // Default to cost when year1 data is available and priority is balanced
    performanceMetric = 'cost';
  }
  
  // Find winner based on metric
  if (performanceMetric === 'cost' && hasYear1Data) {
    // Lowest running cost
    const optionsWithYear1 = allOptions.filter(opt => output.year1?.options?.some(y1 => y1.optionId === opt.id));
    
    if (optionsWithYear1.length > 0 && output.year1?.options) {
      const validCostEntries = optionsWithYear1
        .map(option => {
          const year1 = output.year1?.options?.find(y => y.optionId === option.id);
          return {
            option,
            annualCost: year1?.impact.annualCostGBP,
          };
        })
        .filter(entry => isFiniteNumber(entry.annualCost));

      if (validCostEntries.length > 0) {
        const lowestCostEntry = [...validCostEntries].sort((a, b) => a.annualCost - b.annualCost)[0];
        perfLine = `Lowest running cost is ${lowestCostEntry.option.title}: £${Math.round(lowestCostEntry.annualCost)} per year.`;
      } else {
        perfLine = 'Running cost estimate unavailable – annual consumption not provided.';
      }
    }
  } else if (performanceMetric === 'carbon' && hasYear1Data) {
    // Lowest carbon emissions
    const optionsWithYear1 = allOptions.filter(opt => output.year1?.options?.some(y1 => y1.optionId === opt.id));
    
    if (optionsWithYear1.length > 0) {
      // Sort by year-1 carbon emissions
      const sorted = [...optionsWithYear1].sort((a, b) => {
        const aYear1 = output.year1!.options.find(y => y.optionId === a.id);
        const bYear1 = output.year1!.options.find(y => y.optionId === b.id);
        return (aYear1?.impact.annualCarbonKg || Infinity) - (bYear1?.impact.annualCarbonKg || Infinity);
      });
      
      const lowestCarbon = sorted[0];
      const year1Data = output.year1.options.find(y => y.optionId === lowestCarbon.id);
      
      if (year1Data && isFiniteNumber(year1Data.impact.annualCarbonKg)) {
        perfLine = `Lowest carbon is ${lowestCarbon.title}: ${Math.round(year1Data.impact.annualCarbonKg)} kg CO₂ per year.`;
      }
    }
  }
  
  // Fallback to score-based if no year1 data or metric-based approach failed
  let bestPerf: RecommendationOption | undefined;
  if (!perfLine) {
    const sortedByScore = [...allOptions].sort((a, b) => b.score - a.score);
    bestPerf = sortedByScore[0];
    
    if (bestPerf) {
      // Extract top reasons from pros or whyThisWon
      const reasons = bestPerf.whyThisWon?.slice(0, 2) || bestPerf.pros.slice(0, 2);
      const reasonsText = reasons.length > 0 ? `due to ${reasons.join(' and ')}` : '';
      perfLine = `Best overall (weighted) is ${bestPerf.title}${reasonsText ? ' ' + reasonsText : ''}.`;
    }
  }
  
  // Best space analysis
  let spaceLine: string | null = null;
  // Combis and storage combis are best for space
  const spaceOptions = allOptions.filter(opt => 
    opt.systemType === 'combi' || opt.boiler === 'combi' || opt.boiler === 'storage_combi'
  );
  
  if (spaceOptions.length > 0) {
    const bestSpace = spaceOptions[0];
    spaceLine = `Best space outcome is ${bestSpace.title} — no cylinder required, maximizes usable space.`;
  } else {
    // If no combi, highlight the most space-efficient option
    const regularOrSystem = allOptions.find(opt => opt.systemType === 'system' || opt.boiler === 'system');
    if (regularOrSystem) {
      spaceLine = `Best space outcome is ${regularOrSystem.title} — compact cylinder solution.`;
    }
  }
  
  // Build combined paragraph
  const parts: string[] = [];
  if (valueLine) {
    parts.push(valueLine);
  }
  if (perfLine) {
    parts.push(perfLine);
  }
  if (spaceLine) {
    parts.push(spaceLine);
  }
  
  // Add conditional combi advice if combi exists but isn't best performer
  const hasCombi = allOptions.some(opt => opt.systemType === 'combi' || opt.boiler === 'combi');
  const combiIsBestPerf = bestPerf && (bestPerf.systemType === 'combi' || bestPerf.boiler === 'combi');
  
  if (hasCombi && !combiIsBestPerf && valueLine) {
    parts.push("If you want maximum space and accept a performance trade-off, a combi can still be a good choice.");
  }
  
  const paragraph = parts.length > 0 ? parts.join(' ') : null;
  
  return {
    valueLine,
    perfLine,
    spaceLine,
    paragraph
  };
}
